import _ from 'lodash'

import { CONDITIONS_VALUES } from 'modules/core/components/MultiFilters/FilterItem/useFilterItem'
import MultiFilterForm from 'modules/core/components/MultiFilters/Forms/MultiFilterForm'
import { legalEntityOptions, teamOptions } from 'modules/core/components/MultiFilters/commonOptions'
import genericUseMultiFilters from 'modules/core/components/MultiFilters/hooks/useMultiFilters'
import type { ExtraHooks } from 'modules/core/components/MultiFilters/hooks/utils/applyFilters'
import type { Value } from 'modules/core/components/MultiFilters/hooks/utils/defaultEvaluators'
import type { FilterOption } from 'modules/core/components/MultiFilters/types'
import SafeJSON from 'modules/core/lib/SafeJSON'
import SafeLocalStorage from 'modules/core/lib/SafeLocalStorage'
import { employeeLegalEntityId } from 'modules/core/lib/employeeLegalEntity'
import i18n from 'modules/core/lib/i18n'
import users from 'modules/core/store/collections/users'

import employees from 'modules/employees/store/collections/employees'
import type Employee from 'modules/employees/store/models/Employee'
import memberships from 'modules/teams/store/collections/memberships'
import teams from 'modules/teams/store/collections/teams'
import { employeeTeams } from 'modules/teams/store/employeeTeams'

export type FilterType = 'legal_entity' | 'manager_id' | 'team' | 'employee_status'

const employeeStatuses = {
  active: 'employee_active',
  terminated: 'employee_terminated',
}

const HAS_FILTER_QTY = 1
const HAS_DEFAULT_FILTER_QTY = 1
const DEFAULT_FILTER_VALUE = 'employee_status'
const DEFAULT_OPTION_VALUE = employeeStatuses.active

function getFilterValue(employee: Employee, value: FilterType) {
  if (!employee) return
  switch (value) {
    case 'legal_entity':
      return employeeLegalEntityId(employee)

    case 'manager_id':
      return employee.manager?.attributes.id

    case 'team':
      return employeeTeams(teams, memberships, employee.id).map((x) => x.attributes.id)

    case 'employee_status':
      return employee

    default:
      return false
  }
}

const employeeEvaluator = (employee: Employee, filterValue: Value): boolean => {
  if (!employee) return false

  switch (filterValue) {
    case employeeStatuses.active:
      return !employee.isTerminating
    case employeeStatuses.terminated:
      return employee.isTerminating
    default:
      return false
  }
}

const buildExtraHooks = (): ExtraHooks => {
  return {
    beforeFilter: () => Promise.resolve(),
    skipModelFilter: () => false,
    getDefaultFilters: (filters: MultiFilterForm[]) => {
      const isFilteringByStatus = filters.some(
        (filter) => filter.get('field').value === 'employee_status'
      )
      if (isFilteringByStatus) {
        return null
      }

      return [
        new MultiFilterForm({
          condition: CONDITIONS_VALUES.isEqual,
          field: 'employee_status',
          value: employeeStatuses.active,
        }),
      ]
    },
    customEvaluators: {
      employee_status: {
        is_equal: employeeEvaluator,
      },
    },
  }
}

type FilterValue = {
  filters: Array<MultiFilterForm['values']>
  logicOperator: Record<string, string>
  default: Array<MultiFilterForm['values']>
}

export default function useEmployeesMultifilters(localStorageKey: string, employees: Employee[]) {
  const { filteredModels, onApplyFilters, filters } = genericUseMultiFilters<Employee>(
    getFilterValue,
    localStorageKey,
    buildExtraHooks()
  )

  const persistedFilters: FilterValue | null = SafeJSON.parse<FilterValue | null>(
    SafeLocalStorage.getItem(localStorageKey, null),
    null
  )
  const defaultFilters = persistedFilters?.default ?? []
  const defaultFilter = defaultFilters?.[0]

  const applyDefaultFilters = () => {
    if (!defaultFilter) {
      onApplyFilters(employees)
      return
    }
    onApplyFilters(employees, [new MultiFilterForm(defaultFilter)])
  }

  const getFilterQuantity = (): number => {
    if (!defaultFilter) return filters.length

    if (
      defaultFilter.field === DEFAULT_FILTER_VALUE &&
      defaultFilter.value === DEFAULT_OPTION_VALUE
    ) {
      return HAS_DEFAULT_FILTER_QTY
    } else {
      return HAS_FILTER_QTY
    }
  }

  return {
    filteredEmployees: filteredModels,
    onApplyFilters,
    applyDefaultFilters,
    getFilterQuantity,
    getAppliedFilter: () => defaultFilter,
  }
}

export const filterFields = async (): Promise<FilterOption[]> => {
  return [
    {
      label: i18n.t('filters.multi_filters.legal_entity'),
      value: 'legal_entity',
      type: 'single_choice',
      options: await legalEntityOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.manager'),
      value: 'manager_id',
      type: 'single_choice',
      options: await managerOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.team'),
      value: 'team',
      type: 'single_choice',
      options: await teamOptions(),
    },
    {
      label: i18n.t('filters.multi_filters.employee_status'),
      value: 'employee_status',
      type: 'equal_choice',
      options: [
        {
          value: employeeStatuses.active,
          label: i18n.t('employees.active'),
        },
        {
          value: employeeStatuses.terminated,
          label: i18n.t('employees.terminated'),
        },
      ],
    },
  ]
}

export interface OptionInterface {
  value: boolean | string
  label: string | undefined
}

function managerOptions() {
  const managers = employees.toArray().map((employee) => employee.manager)
  const uniqueManagers = _.uniqBy(managers, 'attributes.id')

  const options: OptionInterface[] = []

  uniqueManagers.forEach((manager) => {
    let label = ''
    if (manager) {
      const userId = manager.attributes.access_id
      const user = users.get(userId)
      if (user) {
        label = user.fullName()
      }
    }
    options.push({
      value: String(manager?.attributes.id),
      label,
    })
  })

  return _.sortBy(options, (manager) => manager.label)
}
